import "slick-carousel/slick/slick.css";
import "slick-carousel/slick/slick-theme.css";
import { Modal } from "react-bootstrap";
import { toast } from "sonner";
import { SubmitHandler, useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { z } from "zod";
import { useAcademicYear } from "../../../core/common/context/AcademicYearContext";
import { createLevel } from "../../../api/pedagogicElement";
import withPermission from "../../../core/common/autorisations/withPermission";
import {
  Action,
  buildPermission,
  Resource,
} from "../../../core/common/autorisations/permissionsMap";
import { useEffect, useState } from "react";
import { getAllLevelTypes } from "../../../api/settings";
import { Select } from "antd";

type Props = {
  show: boolean;
  onClose: () => void;
  onSaved?: () => void;
};

const CreateLevelModal = ({ show, onClose, onSaved }: Props) => {
  //Initialisation de l'année academmique actuelle
  const { selectedYear } = useAcademicYear();

  const [allLevelTypes, setAllLevelTypes] = useState<any[]>([]);
  const [isLoading, setIsLoading] = useState(true); // Pour le vrai chargement
  const [hasLoaded, setHasLoaded] = useState(false); // Pour éviter "Chargement..." quand il n'y a juste pas de données

  const createSchema = z.object({
    label: z.string().nonempty("Le libellé du niveau d'étude est requis"),
    cycle: z.string().nonempty("Veuillez sélectionner le cycle"),
    id_typeLevel: z
      .string()
      .nonempty("Veuillez sélectionner le type de niveau"),
    abbreviation: z.string().nonempty("L'abréviation est requis"),
  });

  type FormFields = z.infer<typeof createSchema>;

  const {
    register,
    handleSubmit,
    clearErrors,
    reset,
    setValue,
    watch,
    formState: { errors, isSubmitting },
  } = useForm<FormFields>({
    resolver: zodResolver(createSchema),
  });

  const selectedLevelTypeId = watch("id_typeLevel");

  const syncAbbreviationWithTypeLevel = (typeLevelId?: string) => {
    const selectedTypeLevel = allLevelTypes.find(
      (lvl) => lvl.id === typeLevelId,
    );
    setValue("abbreviation", selectedTypeLevel?.label || "", {
      shouldValidate: true,
    });
  };

  //Traitement après validation du formulaire de création d'un niveau
  const onSubmit: SubmitHandler<FormFields> = async (data) => {
    try {
      const finalData = {
        ...data,
        id_academicYear: selectedYear?.id ?? "", // 👈 Ajout manuel ici
      };
      //Envoi des datas pour création vers le backend
      await createLevel(finalData);

      reset(); //On réinitialise les champs du formulaire
      if (onSaved) onSaved(); // On fait un reload de la liste

      toast.success("Niveau d'étude ajouté !"); //Toast de succès

      onClose(); // On ferme le modal
    } catch (error: any) {
      // Si erreur (Toast Error)
      toast.error(
        error.response?.data?.message ||
          error.response?.data?.error ||
          "Une erreur s'est produit, Réessayer plus tard",
      );
    }
  };

  useEffect(() => {
    setHasLoaded(false);
    setIsLoading(true);

    getAllLevelTypes()
      .then((lvl) => {
        setAllLevelTypes(lvl.data);
        // console.log('Type de niveau', lvl.data)
      })
      .finally(() => {
        setIsLoading(false); //Les données ne sont plus en chargement
        setHasLoaded(true); //Les données ont été chargé avec succès
      });
  }, [show]);

  return (
    <>
      <Modal show={show} onHide={() => onClose()}>
        <Modal.Header closeButton>
          <Modal.Title>Création Niveau d'Étude</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {/* Message d'erreur serveur */}
          {errors.root && (
            <div className="alert alert-danger rounded-pill alert-dismissible fade show">
              {errors.root.message}
              <button
                type="button"
                className="btn-close"
                aria-label="Close"
                onClick={() => clearErrors("root")}
              >
                <i className="fas fa-xmark" />
              </button>
            </div>
          )}
          <form
            id="level-form"
            className="needs-validation"
            onSubmit={handleSubmit(onSubmit)}
          >
            <div className="row row-cols-xxl-5 row-cols-md-6">
              <div className="col-xxl-12 col-xl-12 col-md-12">
                <div className="mb-3">
                  <label className="form-label">Cycle d'étude</label>
                  <select
                    {...register("cycle")}
                    aria-label="Default select example"
                    className={
                      errors.cycle ? "form-select is-invalid" : "form-select"
                    }
                  >
                    <option value={""}>Sélectionner</option>
                    <option value={"Licence"}>Licence</option>
                    <option value={"Master"}>Master</option>
                  </select>
                  {errors.cycle && (
                    <div className="invalid-feedback">
                      {errors.cycle.message}
                    </div>
                  )}
                </div>
              </div>
              <div className="col-xxl-12 col-xl-12 col-md-12">
                <div className="mb-3">
                  <label className="form-label" htmlFor="validationServer01">
                    Types de niveau
                  </label>
                  <input type="hidden" {...register("id_typeLevel")} />
                  <Select
                    showSearch
                    allowClear
                    size="large"
                    optionFilterProp="label"
                    removeIcon={<span style={{ fontSize: 12 }}>×</span>}
                    style={{ width: "100%" }}
                    getPopupContainer={(trigger) =>
                      trigger.parentElement as HTMLElement
                    }
                    dropdownStyle={{ zIndex: 2000 }}
                    placeholder="Sélectionner le type de niveau"
                    value={selectedLevelTypeId || undefined}
                    onChange={(value) => {
                      setValue("id_typeLevel", value || "", {
                        shouldValidate: true,
                      });
                      syncAbbreviationWithTypeLevel(value);
                    }}
                    onClear={() => {
                      setValue("id_typeLevel", "", { shouldValidate: true });
                      setValue("abbreviation", "", { shouldValidate: true });
                    }}
                  >
                    {allLevelTypes.map((lvl) => (
                      <Select.Option
                        key={lvl.id}
                        value={lvl.id}
                        label={`${lvl.label ?? ""}`}
                      >
                        {lvl.label}
                      </Select.Option>
                    ))}
                  </Select>
                  {errors.id_typeLevel && (
                    <div className="invalid-feedback d-block">
                      {errors.id_typeLevel.message}
                    </div>
                  )}
                </div>
              </div>
              <div className="col-xxl-6 col-xl-6 col-md-6">
                <div className="mb-3">
                  <label className="form-label" htmlFor="validationServer01">
                    Libellé
                  </label>
                  <input
                    {...register("label")}
                    type="text"
                    className={
                      errors.label ? "form-control is-invalid" : "form-control"
                    }
                    placeholder="Ex: Licence 1 ou Licence 2"
                  />
                  {errors.label && (
                    <div className="invalid-feedback">
                      {errors.label.message}
                    </div>
                  )}
                </div>
              </div>

              <div className="col-xxl-6 col-xl-6 col-md-6">
                <div className="mb-3">
                  <label className="form-label" htmlFor="validationServer01">
                    Abréviation
                  </label>
                  <input
                    {...register("abbreviation")}
                    type="text"
                    className={
                      errors.abbreviation
                        ? "form-control is-invalid"
                        : "form-control"
                    }
                    placeholder="Ex: L1 ou L2"
                  />
                  {errors.abbreviation && (
                    <div className="invalid-feedback">
                      {errors.abbreviation.message}
                    </div>
                  )}
                </div>
              </div>
            </div>
          </form>
        </Modal.Body>
        <Modal.Footer>
          <button
            type="button"
            className="btn btn-light me-3"
            onClick={() => {
              reset();
              onClose();
            }}
          >
            Annuler
          </button>

          {isSubmitting ? (
            <button
              className="btn btn-primary-light"
              type="button"
              disabled={false}
            >
              <span
                className="spinner-border spinner-border-sm align-middle me-1"
                role="status"
                aria-hidden="true"
              />
              Chargement...
            </button>
          ) : (
            <button
              type="submit"
              form="level-form" // 🔗 liaison au formulaire
              className="btn btn-primary"
              disabled={isSubmitting}
            >
              Enregistrer
            </button>
          )}
        </Modal.Footer>
      </Modal>
    </>
  );
};

export default withPermission(
  CreateLevelModal,
  [buildPermission(Resource.LEVEL, Action.CREATE)],
  "masquer",
);
